import React, { useRef, useState, useEffect } from "react"
import MerchantList from "./merchant-list"
import axios from "axios"
import Spinner from "../../../booking/components/spinner"
import { setQueryStringValue, setQueryStringWithoutPageReload, clearQueryParameters } from "../../../api/queryStringHelper"
import * as types from "./merchant-types"
import Categories from "./categories"
import { MemoizedRenderComponents } from "../../../helpers/contentAreaRender"
import useWindowWidth from "../../../hooks/useWindowWidth"
import useKeyboardNavigation from "../../../hooks/useKeyboardNavigation"

export default function MerchantListPage({
  title,
  headline,
  subtitle,
  heroImage,
  heroVideo,
  heroVideoDescription,
  categoriesField,
  locationsField,
  resultsLabel,
  searchBoxPlaceholder,
  groupMerchants,
  allShops,
  merchantGroupShops,
  allDiners,
  merchantGroupDiners,
  taxFreeShops,
  allMerchants,
  kind,
  hideShoppingTab,
  shoppingTabLabel,
  hideDiningTab,
  diningTabLabel,
  hideTaxFreeTab,
  taxFreeTabLabel,
  shopsMessageCards,
  shopsVideoCards,
  diningMessageCards,
  diningVideoCards,
  taxFreeMessageCards,
  taxFreeVideoCards,
  noneFoundHeader,
  noneFoundText,
  openTodayLabel,
  alwaysOpen,
  closedText,
  showMoreLabel,
  filterListLabel,
  isTaxFree,
  dataUrl,
  shopsContentId,
  shopsUrl,
  diningContentId,
  diningUrl,
  taxfreeContentId,
  taxfreeUrl,
  showingAmountLabel,
  loadMoreLabel,
  shopCategories,
  shopCategoriesMobile,
  dinerCategories,
  dinerCategoriesMobile,
  locations,
  clearFilteringLabel,
  selectFilteringLabel,
  clearSearchBoxAriaLabel,
  locationLabel,
  taxfreePage,
  shopsPage,
  diningPage,
}) {
  const [merchants, setMerchants] = useState([])
  const [currentAllMerchants, setCurrentAllMerchants] = useState([])
  const [numFound, setNumFound] = useState([])
  const [pageNumber, setPageNumber] = useState(0)
  const [amountShown, setAmountShown] = useState(24)
  const [ajaxSpinnerActive, setAjaxSpinnerActive] = useState(false)
  const [ajaxSpinnerTransparent, setAjaxSpinnerTransparent] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [timeout, setTimeoutState] = useState(undefined)
  const [filters, setFilters] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedLocations, setSelectedLocations] = useState("")
  const windowWidth = useWindowWidth()
  const isKeyboardNavigating = useKeyboardNavigation()

  const shopListRef = useRef(null)

  const getCategories = () => {
    if (windowWidth < 950) {
      return isTaxFree === true ? [] : kind === "Kind_s:Shops" ? shopCategoriesMobile : dinerCategoriesMobile
    } else {
      return isTaxFree === true ? [] : kind === "Kind_s:Shops" ? shopCategories : dinerCategories
    }
  }

  const [activeTabLabel, setActiveTabLabel] = useState(
    isTaxFree === true ? taxFreeTabLabel : kind === "Kind_s:Shops" ? shoppingTabLabel : diningTabLabel,
  )
  const [categories, setCategories] = useState(getCategories())
  const [merchantType, setMerchantType] = useState(
    kind === "Kind_s:*" ? types.BOTH : isTaxFree ? types.TAX_FREE : kind === "Kind_s:Shops" ? types.SHOPS : types.DINERS,
  )
  const [contentArea, setContentArea] = useState(
    isTaxFree === true ? taxfreePage?.contentArea : kind === "Kind_s:Shops" ? shopsPage?.contentArea : diningPage?.contentArea,
  )
  const [hideSearch, setHideSearch] = useState(
    isTaxFree === true ? taxfreePage?.hideSearchField : kind === "Kind_s:Shops" ? shopsPage?.hideSearchField : diningPage?.hideSearchField,
  )
  const [hideLocations, setHideLocations] = useState(
    isTaxFree === true ? taxfreePage?.hideLocations : kind === "Kind_s:Shops" ? shopsPage?.hideLocations : diningPage?.hideLocations,
  )

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const distinctKeys = [...new Set(searchParams.keys())]
    const filters = []
    let searchQuery = ""
    let locationFilters = ""
    let categoryFilters = []

    distinctKeys.forEach((key) => {
      switch (key) {
        case "CategoriesText":
          categoryFilters = searchParams.getAll(key)
          filters.push({ key: "categories", value: categoryFilters })
          break
        case "ShopLocation":
          locationFilters = searchParams.get(key)
          filters.push({ key: "locations", value: locationFilters })
          break
        case "query":
          searchQuery = searchParams.get(key)
          filters.push({ key: "name", value: searchQuery })
          break
      }
    })

    fetchDataBasedOnType(merchantType, filters, locationFilters, searchQuery, categoryFilters)
  }, [merchantType, kind, isTaxFree])

  useEffect(() => {
    let currentAllMerchants = []
    let merchants = []

    switch (merchantType) {
      case types.BOTH:
        currentAllMerchants = allMerchants
        merchants = applyFilters(allMerchants, filters)
        break
      case types.TAX_FREE:
        currentAllMerchants = taxFreeShops
        merchants = applyFilters(taxFreeShops, filters)
        break
      case types.SHOPS:
        currentAllMerchants = !groupMerchants ? allShops : merchantGroupShops
        merchants = applyFilters(!groupMerchants ? allShops : merchantGroupShops, filters)
        break
      case types.DINERS:
        currentAllMerchants = !groupMerchants ? allDiners : merchantGroupDiners
        merchants = applyFilters(!groupMerchants ? allDiners : merchantGroupDiners, filters)
        break
    }
    setCurrentAllMerchants(currentAllMerchants)
    setMerchants(merchants)
    setNumFound(merchants.length)
  }, [filters, merchantType, groupMerchants, allMerchants, taxFreeShops, allShops, merchantGroupShops, allDiners, merchantGroupDiners])

  useEffect(() => {
    setCategories(getCategories())
  }, [windowWidth])

  const fetchDataBasedOnType = (type, filters, locationFilters, searchQuery, categoryFilters) => {
    let dataSets = {
      [types.TAX_FREE]: () => setMerchantData(taxFreeShops, taxFreeShops, filters, locationFilters, searchQuery, categoryFilters),
      [types.SHOPS]: () => setMerchantData(merchantGroupShops, allShops, filters, locationFilters, searchQuery, categoryFilters),
      [types.DINERS]: () => setMerchantData(merchantGroupDiners, allDiners, filters, locationFilters, searchQuery, categoryFilters),
      [types.BOTH]: () => setMerchantData(allMerchants, allMerchants, filters, locationFilters, searchQuery, categoryFilters),
    }

    dataSets[type] && dataSets[type]()
  }

  const setMerchantData = (shops, allShops, filters, locationFilters, searchQuery, categoryFilters) => {
    let merchants = []

    if (filters.length > 0) {
      merchants = applyFilters(allShops, filters)
    } else {
      merchants = shops
    }

    setMerchants(merchants)
    setNumFound(merchants.length)
    setFilters(filters)
    setSelectedLocations(locationFilters)
    setSearchQuery(searchQuery)
    setSelectedCategories(categoryFilters)
  }

  const applyFilters = (shops, filters) => {
    return shops.filter((item) => {
      for (const filter of filters) {
        const filterKey = filter.key
        const filterValue = filter.value

        switch (filterKey) {
          case "locations":
            if (filterValue === "before-security" && !item.isAirSide) {
              continue
            } else if (filterValue === "after-security" && item.isAirSide) {
              continue
            } else {
              const locations = filterValue.split(",")
              if (!locations.includes(item.shopLocation)) {
                return false
              }
            }
            break
          case "categories":
            if (!item.categories?.some((c) => filterValue.includes(c))) {
              return false
            }
            break
          case "name":
            {
              let found = false
              if (item.name?.toLowerCase().includes(filterValue.toLowerCase())) {
                found = true
              }
              if (item.description?.toLowerCase().includes(filterValue.toLowerCase())) {
                found = true
              }
              if (item?.metaKeywords?.some((m) => m.toLowerCase().includes(filterValue.toLowerCase()))) {
                found = true
              }
              if (item?.categories?.some((c) => c.toLowerCase().includes(filterValue.toLowerCase()))) {
                found = true
              }
              if (!found) {
                return false
              }
            }
            break

          default:
            break
        }
      }
      return true
    })
  }

  const writeFilterToState = (filter) => {
    setFilters((currentFilters) => {
      let updatedFilters = currentFilters ? currentFilters?.filter((f) => f.key !== filter.key) : []
      switch (filter.key) {
        case "name":
          if (filter.value) {
            setQueryStringValue("query", filter.value)
            updatedFilters = [...updatedFilters, filter]
          } else {
            const params = new URLSearchParams(window.location.search)
            params.delete("query")
            if (params.size > 0) {
              setQueryStringWithoutPageReload(`?${params.toString()}`)
            } else {
              clearQueryParameters()
            }
          }
          break
        case "locations":
          if (filter.value) {
            setQueryStringValue(locationsField, filter.value)
            updatedFilters = [...updatedFilters, filter]
          } else {
            const params = new URLSearchParams(window.location.search)
            params.delete(locationsField)
            if (params.size > 0) {
              setQueryStringWithoutPageReload(`?${params.toString()}`)
            } else {
              clearQueryParameters()
            }
          }
          break
        case "categories":
          if (filter.value.length > 0) {
            updatedFilters = [...updatedFilters, filter]
          }
          break
        default:
          updatedFilters = [...updatedFilters, filter]
          break
      }
      return updatedFilters
    })
  }

  const loadMore = () => {
    // when loading more shops, we focus on the last focusable shop in the list, before adding additional shops to the list.
    // such that the user doesn't have to go through the list twice.
    var shoplist = shopListRef.current
    if (shoplist && shoplist.childElementCount > 0) {
      var searchIndex = 1
      var shopListLength = shoplist.childElementCount - searchIndex
      var targetShop = shoplist.childNodes[shopListLength - searchIndex]

      // if a shop is deactivated, we make sure that it cannot be focused when loading more.
      // so we go one step back in the list until we find a shop that isnt deactivated.
      while (targetShop.classList.contains("deactivated")) {
        searchIndex += 1
        targetShop = shoplist.childNodes[shopListLength - searchIndex]
      }
      targetShop.focus()
    }

    setPageNumber((prev) => prev + 1)
  }

  const handleKeyPressSwitchTabs = (keypress, type) => {
    if (keypress == " " || keypress == "32" || keypress == "Enter" || keypress == "13") {
      switchTabs(type)
    }
    return
  }

  const switchTabs = (type) => {
    if (type !== merchantType) {
      setAjaxSpinnerActive(true)
      let contentId = ""
      switch (type) {
        case types.TAX_FREE:
          contentId = taxfreeContentId
          setTabSwitchData(
            type,
            taxFreeShops,
            taxFreeTabLabel,
            [],
            taxfreePage?.contentArea,
            taxfreePage?.hideSearchField,
            taxfreePage?.hideLocations,
          )
          break
        case types.SHOPS:
          contentId = shopsContentId
          setTabSwitchData(
            type,
            merchantGroupShops,
            shoppingTabLabel,
            windowWidth < 950 ? shopCategoriesMobile : shopCategories,
            shopsPage?.contentArea,
            shopsPage?.hideSearchField,
            shopsPage?.hideLocations,
          )
          break
        case types.DINERS:
          contentId = diningContentId
          setTabSwitchData(
            type,
            merchantGroupDiners,
            diningTabLabel,
            windowWidth < 950 ? dinerCategoriesMobile : dinerCategories,
            diningPage?.contentArea,
            diningPage?.hideSearchField,
            diningPage?.hideLocations,
          )
          break
      }
      // Side effects to update URL and page title after state updates
      updateUrl(type)
      updatePageTitle(contentId)
    }
  }

  const setTabSwitchData = (type, merchants, tabLabel, categories, contentArea, hideSearch, hideLocations) => {
    setMerchants(merchants)
    setCategories(categories)
    setFilters([])
    setSelectedCategories([])
    setSelectedLocations("")
    setSearchQuery("")
    setMerchantType(type)
    setPageNumber(0)
    setNumFound(merchants.length)
    setActiveTabLabel(tabLabel)
    setContentArea(contentArea)
    setHideSearch(hideSearch)
    setHideLocations(hideLocations)
    setAjaxSpinnerActive(false)
  }

  const selectCategory = (categoryValue) => {
    updateFilterQueryString(categoriesField, categoryValue)

    setSelectedCategories((currentSelectedCategories) => {
      let newSelectedCategories = [...currentSelectedCategories]

      if (currentSelectedCategories.includes(categoryValue)) {
        newSelectedCategories = currentSelectedCategories.filter((cat) => cat !== categoryValue)
      } else {
        newSelectedCategories.push(categoryValue)
      }

      // Post-update side effect
      writeFilterToState({ key: "categories", value: newSelectedCategories })

      return newSelectedCategories
    })
  }

  const selectLocations = (locationValue) => {
    setSelectedLocations(locationValue)
    writeFilterToState({ key: "locations", value: locationValue })
  }

  const searchInputHandler = (e) => {
    const query = e?.target?.value || ""
    if (timeout) {
      clearTimeout(timeout)
      setTimeoutState(undefined)
    }
    setTimeoutState(
      setTimeout(() => {
        writeFilterToState({ key: "name", value: query })
      }, 250),
    )
    setSearchQuery(query)
  }

  const updateFilterQueryString = (key, value) => {
    const params = new URLSearchParams(window.location.search)
    const filters = params.getAll(key)

    if (filters.includes(value)) {
      // Remove value if it exists
      const newFilters = filters.filter((item) => item !== value)
      params.delete(key)
      newFilters.forEach((item) => params.append(key, item))
    } else {
      // Add value if it doesn't exist
      params.append(key, value)
    }

    setQueryStringWithoutPageReload(`?${params.toString()}`)
  }

  const updatePageTitle = (contentLink) => {
    axios
      .get(`${dataUrl}/GetPageTitle?contentLink=${contentLink}`)
      .then((res) => {
        if (res.data) {
          document.title = res.data
        }
      })
      .catch((error) => console.log(error))
      .finally(() => setAjaxSpinnerActive(false))
  }

  const updateUrl = (type) => {
    let path = ""
    switch (type) {
      case types.TAX_FREE:
        path = taxfreeUrl
        break
      case types.SHOPS:
        path = shopsUrl
        break
      case types.DINERS:
        path = diningUrl
        break
    }
    const newUrl = `${window.location.protocol}//${window.location.host}${path}`
    window.history.replaceState({ path: newUrl }, "", newUrl)
  }

  const clearFilters = () => {
    clearQueryParameters()
    setFilters([])
    setSelectedCategories([])
    setSelectedLocations("")
    setSearchQuery("")
  }

  let searchBoxPlaceholderFinal
  let loadMoreLabelFinal = loadMoreLabel ?? "Vis flere {name}"
  let messageCards
  let videoCards
  switch (merchantType) {
    case types.TAX_FREE:
      searchBoxPlaceholderFinal = searchBoxPlaceholder?.replace("{name}", taxFreeTabLabel)
      loadMoreLabelFinal = loadMoreLabelFinal?.replace("{name}", taxFreeTabLabel)
      messageCards = taxFreeMessageCards
      videoCards = taxFreeVideoCards
      break
    case types.SHOPS:
      searchBoxPlaceholderFinal = searchBoxPlaceholder?.replace("{name}", shoppingTabLabel?.toLowerCase())
      loadMoreLabelFinal = loadMoreLabelFinal?.replace("{name}", shoppingTabLabel?.toLowerCase())
      messageCards = shopsMessageCards
      videoCards = shopsVideoCards
      break
    case types.DINERS:
      searchBoxPlaceholderFinal = searchBoxPlaceholder?.replace("{name}", diningTabLabel?.toLowerCase())
      loadMoreLabelFinal = loadMoreLabelFinal?.replace("{name}", diningTabLabel?.toLowerCase())
      messageCards = diningMessageCards
      videoCards = diningVideoCards
      break
  }

  return (
    <React.Fragment>
      <Spinner isActive={ajaxSpinnerActive} isTransparent={ajaxSpinnerTransparent} />
      <div className="merchant-list-page">
        <section className="merchant-list-page--hero">
          <div className="w--wider">
            <div className="tabs-section">
              <div className="head">
                <span className="infohead">{title}</span>
                <h1>{headline}</h1>
                <p>{subtitle}</p>
              </div>
              <div className="tabs" role="tablist">
                {!hideShoppingTab && (
                  <a
                    tabIndex={0}
                    className={`button${merchantType == types.SHOPS ? " is-active" : ""}`}
                    aria-selected={merchantType == types.SHOPS}
                    onClick={() => switchTabs(types.SHOPS)}
                    onKeyDown={(e) => handleKeyPressSwitchTabs(e.key, types.SHOPS)}
                    role="tab"
                    aria-controls="element 1"
                  >
                    <span className="text" data-epi-edit="ShoppingTabTitle">
                      {shoppingTabLabel || "Butikker"}
                    </span>
                  </a>
                )}
                {!hideDiningTab && (
                  <a
                    tabIndex={0}
                    className={`button${merchantType == types.DINERS ? " is-active" : ""}`}
                    aria-selected={merchantType == types.DINERS}
                    onClick={() => switchTabs(types.DINERS)}
                    onKeyDown={(e) => handleKeyPressSwitchTabs(e.key, types.DINERS)}
                    role="tab"
                    aria-controls="element 2"
                  >
                    <span className="text" data-epi-edit="DiningTabTitle">
                      {diningTabLabel || "Spisesteder"}
                    </span>
                  </a>
                )}
                {!hideTaxFreeTab && (
                  <a
                    tabIndex={0}
                    className={`button${merchantType == types.TAX_FREE ? " is-active" : ""}`}
                    aria-selected={merchantType == types.TAX_FREE}
                    onClick={() => switchTabs(types.TAX_FREE)}
                    onKeyDown={(e) => handleKeyPressSwitchTabs(e.key, types.TAX_FREE)}
                    role="tab"
                    aria-controls="element 3"
                  >
                    <span className="text" data-epi-edit="TaxFreeTabTitle">
                      {taxFreeTabLabel || "TAX FREE"}
                    </span>
                  </a>
                )}
              </div>
            </div>
            <div className="image-wrapper">
              {heroVideo ? (
                <video
                  tabIndex={heroVideoDescription ? 0 : -1}
                  aria-label={heroVideoDescription}
                  autoPlay
                  loop
                  muted
                  controls={false}
                  playsInline
                  preload={"auto"}
                  loading="eager"
                  fetchPriority="high"
                >
                  <source src={heroVideo} />
                </video>
              ) : heroImage ? (
                <img src={heroImage} />
              ) : (
                ""
              )}
            </div>
          </div>
        </section>
        {!hideSearch ? (
          <section className="filtering w--wider">
            <div className="search-field">
              <input
                className="search-input"
                value={searchQuery}
                onChange={(e) => searchInputHandler(e)}
                type="text"
                placeholder={searchBoxPlaceholderFinal}
              />
              <button
                tabIndex={searchQuery.length == 0 ? -1 : 0}
                onClick={() => {
                  searchInputHandler("")
                }}
                aria-label={clearSearchBoxAriaLabel}
                className={`search-button ${searchQuery.length >= 1 ? "active" : ""}`}
              >
                <div className="search-icon"></div>
              </button>
            </div>
            <Categories
              categories={categories}
              toggleCategory={selectCategory}
              merchants={currentAllMerchants}
              numFound={numFound}
              selectedCategories={selectedCategories}
              searchQuery={searchQuery}
              windowWidth={windowWidth}
              merchantType={merchantType}
            />
          </section>
        ) : (
          ""
        )}
        <section className="deck deck--white deck--remove-z-index">
          <MerchantList
            shopListRef={shopListRef}
            merchants={merchants}
            messageCards={messageCards}
            videoCards={videoCards}
            numFound={numFound}
            noneFoundHeader={noneFoundHeader || ""}
            noneFoundText={noneFoundText || ""}
            page={pageNumber}
            amountShown={amountShown}
            resultsLabel={resultsLabel}
            loadMore={loadMore}
            loadMoreLabel={loadMoreLabelFinal}
            showingAmountLabel={showingAmountLabel}
            activeTabLabel={activeTabLabel}
            locations={locations}
            selectLocations={selectLocations}
            selectedLocations={selectedLocations}
            clearFilteringLabel={clearFilteringLabel || "Ryd filtrering"}
            selectFilteringLabel={selectFilteringLabel || "Vælg"}
            filters={filters}
            clearFilters={clearFilters}
            currentAllMerchants={currentAllMerchants}
            hideVideoCards={filters?.length > 0}
            locationLabel={locationLabel || "Placering"}
            isKeyboardNavigating={isKeyboardNavigating}
            hideLocations={hideLocations}
          />
        </section>
      </div>
      {contentArea && contentArea.length > 0 ? <MemoizedRenderComponents contentArea={contentArea} /> : ""}
    </React.Fragment>
  )
}
